<template>
  <main :class="[cssClass]" role="main">
    <nav role="navigation" aria-label="parallax navigation" :class="[`${cssClass}-nav`]" :data-nav-position="navPosition" v-if="navType === 'buttons'">
      <form>
        <ul>
          <li v-for="(item, index) in nav" :key="index" v-if="nav.length">
            <label :for="label(item)" :title="index + 1">
              <input :id="label(item)" aria-hidden="true" type="radio" :name="cssClass" :value="item" v-model="currentSection" /><span />
            </label>
          </li>
        </ul>
      </form>
    </nav>
    <div :class="[`${cssClass}-sections`]">
      <slot />
      <vc-style>
        html,
        body,
        ::-webkit-scrollbar
        {
          background: {{ navColor }};
        }

        ::-webkit-scrollbar-corner,
        ::-webkit-scrollbar-thumb
        {
          background: {{ navAccentColor }};
        }

        .vc-parallax-nav,
        .vc-parallax-nav span
        {
          border: solid 2px {{ navColor }};
        }

        .vc-parallax-nav input:checked + span
        {
          background: {{ navColor }};
        }
      </vc-style>
    </div>
  </main>
</template>
<script>
import VcStyle from './style'

export default {
  name: 'VcParallax',
  components: {
    VcStyle
  },
  data () {
    return {
      nav: [],
      currentSection: null,
      cssClass: 'vc-parallax',
      dataIndex: 'data-index'
    }
  },
  watch: {
    /**
     * Current section watcher
     */
    currentSection () {
      const sectionId = this.currentSection
      const element = document.getElementById(sectionId)
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        })
        this.$emit('section-change', {
          sectionId,
          sectionIndex: parseInt(element.getAttribute(this.dataIndex), 10)
        })
      }
    }
  },
  props: {
    /**
     * Navigation component color
     */
    navColor: {
      type: String,
      required: false,
      default: '#ccc'
    },
    /**
     * Navigation accented color
     */
    navAccentColor: {
      type: String,
      required: false,
      default: '#282828'
    },
    /**
     * Navigation position
     */
    navPosition: {
      type: String,
      required: false,
      default: 'right'
    },
    /**
     * Navigation type ('buttons'|'scroll')
     */
    navType: {
      type: String,
      required: false,
      default: 'buttons'
    }
  },
  created () {
    this.onCreated()
  },
  mounted () {
    this.onMounted()
  },
  beforeDestroy () {
    this.emitEvent('before-destroy')
  },
  methods: {
    /**
     * Handler for the created callback of the component
     * @method onCreated
     * @return {void}
     */
    onCreated () {
      this.$root.$on('navigate-to-section', (index) => {
        this.currentSection = this.nav[index]
      })
    },
    /**
     * Return the component data emitted by component events
     * @method label
     * @params {string} id [id=null]
     * @return {string}
     */
    label (id = null) {
      if (id) {
        return `label-${id}`
      }
    },
    /**
     * Return the component data emitted by component events
     * @method getComponentData
     * @return {object}
     */
    getComponentData () {
      return {
        nav: this.nav,
        props: this.$props,
        sectionId: this.currentSection
      }
    },
    /**
     * Trigger one of the custom events of the component
     * @method emitEvent
     * @param {string} event [event="mounted"]
     * @return {void}
     */
    emitEvent (event = 'mounted') {
      this.$emit(event, this.getComponentData())
    },
    /**
     * Handler for the mounted callback of the component
     * @method onMounted
     * @return {void}
     */
    onMounted () {
      const cssClass = `${this.cssClass}-section`
      const sections = this.$el.querySelectorAll('section')
      const nav = []
      if (!sections.length) {
        return console.error('The <vc-parallax /> component should contain at least one <vc-parallax-section /> element.')
      }
      sections.forEach((section, index) => {
        const id = section.id
        section.classList.add(index % 2 === 0 ? `${cssClass}-parallax` : `${cssClass}-static`)
        section.setAttribute(this.dataIndex, index)
        nav.push(id)
        if (index === 0) {
          this.currentSection = id
        }
      })
      this.nav = nav
      this.emitEvent()
    }
  }
}
</script>
<style>
:root
{
  --em-3: .75em;
  --em-6: 1.5em;
  --em-4: 1em;
  --half: 50%;
  --full: 100%;
  --half-neg: -50%;
}

*
{
  box-sizing: border-box;
}

html,
body,
.vc-parallax-nav,
.vc-parallax-nav *
{
  margin: 0;
  padding: 0;
}

::-webkit-scrollbar,
::-webkit-scrollbar-corner,
::-webkit-scrollbar-thumb
{
  border-radius: var(--em-3);
}

html,
body,
.vc-parallax-section .vc-parallax-section-backdrop
{
  width: var(--full);
}

.vc-parallax-nav,
.vc-parallax-nav *
{
  list-style: none;
}

html,
body,
.vc-parallax-nav ~ .vc-parallax-sections
{
  overflow: hidden;
}

html,
body,
.vc-parallax-section,
.vc-parallax-section .vc-parallax-section-backdrop
{
  height: var(--full);
}

.vc-parallax-nav input,
.vc-parallax-section .vc-parallax-section-backdrop
{
  position: absolute;
}

.vc-parallax-nav[data-nav-position="left"],
.vc-parallax-nav[data-nav-position="right"]
{
  top: var(--half);
  transform: translateY(var(--half-neg));
}

.vc-parallax-nav[data-nav-position="top"],
.vc-parallax-nav[data-nav-position="bottom"]
{
  left: var(--half);
  transform: translateX(var(--half-neg));
}

.vc-parallax-nav[data-nav-position="top"] li,
.vc-parallax-nav[data-nav-position="bottom"] li
{
  display: inline-block;
  vertical-align: middle;
}

.vc-parallax-sections
{
  cursor: default;
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  perspective: 3px;
  text-align: center;
  scroll-behavior: smooth;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.vc-parallax-nav
{
  user-select: none;
  position: fixed;
  z-index: 10;
  background: rgba(0, 0, 0, .5);
  padding: .25em;
  border-radius: calc(.5em * 3);
  backdrop-filter: blur(var(--em-4));
  box-shadow: 0 0 1em rgba(0, 0, 0, .75);
}

.vc-parallax-nav[data-nav-position="top"]
{
  top: var(--em-4);
}

.vc-parallax-nav[data-nav-position="right"]
{
  right: var(--em-4);
}

.vc-parallax-nav[data-nav-position="bottom"]
{
  bottom: var(--em-4);
}

.vc-parallax-nav[data-nav-position="left"]
{
  left: var(--em-4);
}

.vc-parallax-nav input
{
  clip: rect(0 0 0 0);
}

.vc-parallax-nav label
{
  cursor: pointer;
}

.vc-parallax-nav span
{
  display: block;
  margin: .5em;
  border-radius: var(--full);
  width: var(--em-6);
  height: var(--em-6);
  transition: background-color .5s ease-in-out;
}

::-webkit-scrollbar
{
  height: var(--em-3);
  width: var(--em-3);
}

.vc-parallax-section
{
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font: normal normal 2em/2em Helvetica, "Helvetica Neue", Arial, sans-serif;
  padding: 3em;
}

.vc-parallax-section .vc-parallax-section-backdrop
{
  z-index: -1;
  transform: translateZ(-1px) scale(1.5);
  background-size: cover;
  pointer-events: none;
}

.vc-parallax-section.vc-parallax-section-parallax
{
  background-color: transparent !important;
}

.vc-parallax-section.vc-parallax-section-static .vc-parallax-section-backdrop
{
  background-image: none !important;
}
</style>



// WEBPACK FOOTER //
// parallax.vue?4821780e